import React, { useState, useEffect, useCallback , useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, CalendarRange, Users, Building2, BookOpen, MapPin, Hash, Clock, Loader2 } from 'lucide-react';
import { PlanningPedagogiqueTab } from './dossier/PlanningPedagogiqueTab';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { getAuthToken } from '../lib/authToken';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DossierPlanningPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const headers = useMemo(() => ({ Authorization: `Bearer ${getAuthToken()}` }), []);

  const [dossier, setDossier] = useState(null);
  const [formations, setFormations] = useState([]);
  const [modules, setModules] = useState([]);
  const [formateurs, setFormateurs] = useState([]);
  const [lieux, setLieux] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [dossierRes, formationsRes, modulesRes, formateursRes, lieuxRes] = await Promise.all([
        axios.get(`${API_URL}/dossiers/${id}`, { headers }),
        axios.get(`${API_URL}/formations`, { headers }),
        axios.get(`${API_URL}/modules`, { headers }),
        axios.get(`${API_URL}/formateurs`, { headers }),
        axios.get(`${API_URL}/lieux`, { headers }),
      ]);
      setDossier(dossierRes.data);
      setFormations(formationsRes.data);
      setModules(modulesRes.data);
      setFormateurs(formateursRes.data);
      setLieux(lieuxRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement du dossier');
    } finally {
      setLoading(false);
    }
  }, [id, headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePlanningChange = async (newPlanning) => {
    try {
      const res = await axios.patch(`${API_URL}/dossiers/${id}/planning`, { planning: newPlanning }, { headers });
      setDossier(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde du planning');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
        </div>
      </DashboardLayout>
    );
  }

  if (!dossier) {
    return (
      <DashboardLayout>
        <div className="text-center py-20">
          <p className="text-slate-500">Dossier introuvable</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/planning')}>Retour au planning</Button>
        </div>
      </DashboardLayout>
    );
  }

  const formation = formations.find(f => f.id === dossier.formation_id);
  const nbCandidats = (dossier.candidats || []).length;
  const nbJours = (dossier.planning || []).length;
  const formateursAffectes = [...new Set((dossier.planning || []).filter(p => p.formateur_id).map(p => p.formateur_id))];

  const formatDate = (d) => {
    if (!d) return '-';
    try { return format(parseISO(d), 'dd MMM yyyy', { locale: fr }); } catch { return d; }
  };

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="dossier-planning-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/planning')} data-testid="btn-back-planning">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
              Planning pédagogique
            </h1>
            <p className="text-sm text-slate-500">{dossier.code} - {dossier.intitule}</p>
          </div>
        </div>

        {/* Info card */}
        <Card className="border-0 shadow-sm bg-gradient-to-r from-slate-50 to-indigo-50/30">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div className="flex items-start gap-2.5">
                <Hash className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[10px] uppercase text-slate-400 font-medium">Code</p>
                  <p className="text-sm font-semibold text-slate-800">{dossier.code}</p>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <BookOpen className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[10px] uppercase text-slate-400 font-medium">Formation</p>
                  <p className="text-sm font-medium text-slate-800 truncate" title={formation?.titre}>{formation?.code || '-'} - {formation?.titre || '-'}</p>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <Building2 className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[10px] uppercase text-slate-400 font-medium">Client</p>
                  <p className="text-sm font-medium text-slate-800 truncate">{dossier.client || '-'}</p>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <CalendarRange className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[10px] uppercase text-slate-400 font-medium">Période</p>
                  <p className="text-sm font-medium text-slate-800">{formatDate(dossier.date_debut)} → {formatDate(dossier.date_fin)}</p>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <Users className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[10px] uppercase text-slate-400 font-medium">Candidats / Jours</p>
                  <p className="text-sm font-medium text-slate-800">{nbCandidats} candidat(s) · {nbJours} jour(s)</p>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[10px] uppercase text-slate-400 font-medium">Formateurs affectés</p>
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    {formateursAffectes.length === 0 ? (
                      <span className="text-sm text-slate-400">Aucun</span>
                    ) : formateursAffectes.map(fId => {
                      const f = formateurs.find(fo => fo.id === fId);
                      return (
                        <span key={fId} className="text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded font-medium">
                          {f ? `${f.prenom?.[0]}. ${f.nom}` : '?'}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Planning pédagogique */}
        <PlanningPedagogiqueTab
          planning={dossier.planning || []}
          formations={formations}
          formationId={dossier.formation_id}
          modules={modules}
          formateurs={formateurs}
          lieux={lieux}
          onPlanningChange={handlePlanningChange}
          dossierId={id}
          token={getAuthToken()}
          referentId={dossier.referent_id}
          lieuPrincipal={dossier.lieu_principal}
        />
      </div>
    </DashboardLayout>
  );
};

export default DossierPlanningPage;
